import React, { useRef, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Upload, Link as LinkIcon, X, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Convert a possibly-relative URL (e.g. /api/uploads/xxx.jpg) into an absolute URL.
 */
export const resolveImageUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/")) return `${BACKEND_URL}${url}`;
  return url;
};

const ImageUpload = ({ value, onChange, label, placeholder = "URL ou upload" }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const display = resolveImageUrl(value);

  const onPick = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image trop volumineuse (max 8 Mo)");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/admin/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // store the relative path so it works across preview/production
      onChange(data.url);
      toast.success("Image téléversée");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors du téléversement");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div>
      {label && <label className="block text-xs uppercase tracking-widest text-stone-500 mb-1.5">{label}</label>}
      <div className="flex gap-3 items-start">
        <div className="relative w-24 h-24 shrink-0 rounded-xl bg-stone-100 overflow-hidden border border-stone-200 flex items-center justify-center">
          {display ? (
            <img src={display} alt="" className="w-full h-full object-cover" />
          ) : (
            <span className="text-stone-300 text-xs text-center px-2">Aucune image</span>
          )}
          {uploading && (
            <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
              <Loader2 className="animate-spin text-deep-rose" size={20} />
            </div>
          )}
        </div>
        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-2">
            <LinkIcon size={14} className="text-stone-400 shrink-0" />
            <input
              type="text"
              value={value || ""}
              onChange={(e) => onChange(e.target.value)}
              placeholder={placeholder}
              className="flex-1 bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blush"
            />
          </div>
          <div className="flex gap-2">
            <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" />
            <button type="button" onClick={onPick} disabled={uploading} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blush-light text-deep-rose text-xs font-medium disabled:opacity-50">
              <Upload size={14} /> {uploading ? "Envoi..." : "Téléverser une image"}
            </button>
            {value && (
              <button type="button" onClick={() => onChange("")} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-stone-100 text-stone-600 text-xs">
                <X size={14} /> Effacer
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageUpload;
